import * as React from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import { useAuth } from "@/providers";
import { DatasetSelector } from "@/shared/components/DatasetSelector";
import { PageLayout } from "@/shared/components/PageLayout";
import {
  SessionVisibility,
  type TemplateVariable,
  TemplateVariableType,
} from "@/shared/domain/agents";
import { useAgent, useLaunchAgent } from "@/shared/domain/agents/hooks";
import { type OrgRecord } from "@/shared/domain/orgs";
import { Button } from "@/shared/foundation/primitives";

import styles from "./LaunchAgent.module.css";
import { seedLaunchValues } from "./seedLaunchValues";

/**
 * Renders one input per declared variable on the agent, plus a
 * visibility radio, then POSTs to the launch route. On success the user
 * lands on the new thread's detail page so the thread appears immediately.
 * ``DATASET_ID`` variables render via :class:`DatasetSelector`; other
 * types (today just ``STRING``) render as a plain text input with the
 * variable's declared type shown as a badge.
 */
export function LaunchAgent() {
  const navigate = useNavigate();
  const { agentId } = useParams<{ agentId: string }>();
  const [searchParams] = useSearchParams();
  // The dataset-page "Run agent" dialog navigates here with a
  // ``?dataset_id=<id>`` query param so the dataset is pre-filled
  // without a second click. Empty / missing → no pre-fill.
  const datasetIdFromUrl = searchParams.get("dataset_id") ?? "";
  const { currentOrganization } = useAuth();
  const orgId = currentOrganization?.org_id;

  const agentQuery = useAgent(agentId, { orgId });
  const launchMutation = useLaunchAgent();

  const [values, setValues] = React.useState<Record<string, string>>({});
  const [visibility, setVisibility] = React.useState<SessionVisibility>(
    SessionVisibility.ORG,
  );
  const [submitError, setSubmitError] = React.useState<string | null>(null);

  // Seed empty inputs with either the URL-bound dataset_id (when the
  // user came from a dataset detail page's "Run agent" dialog) or the
  // agent's declared defaults. The rules live in `seedLaunchValues`
  // so they're unit-testable without mounting this component.
  //
  // The effect runs on every agentQuery.data change rather than a
  // single seededRef latch — if TanStack Query serves a stale cache
  // first and refetches the fresh copy moments later, the second
  // arrival's seeds still land.
  React.useEffect(() => {
    const agent = agentQuery.data;
    if (agent === undefined) {
      return;
    }
    setValues((prev) =>
      seedLaunchValues(prev, {
        variables: agent.variables,
        datasetIdFromUrl,
      }),
    );
  }, [agentQuery.data, datasetIdFromUrl]);

  const missingRequired = React.useMemo(() => {
    const agent = agentQuery.data;
    if (agent === undefined) {
      return [];
    }
    return agent.variables.filter(
      (v) => v.required && (values[v.name] ?? "") === "",
    );
  }, [agentQuery.data, values]);

  const canSubmit =
    agentQuery.data !== undefined &&
    orgId !== undefined &&
    agentId !== undefined &&
    missingRequired.length === 0 &&
    !launchMutation.isPending;

  const handleSubmit = async () => {
    if (!canSubmit || agentId === undefined || orgId === undefined) {
      return;
    }
    setSubmitError(null);
    try {
      const session = await launchMutation.mutateAsync({
        orgId,
        agentId,
        request: { values, visibility },
      });
      navigate(`/agents/threads/${session.thread_id}`);
    } catch (error) {
      setSubmitError(
        error instanceof Error ? error.message : "Launch failed; please retry.",
      );
    }
  };

  const agent = agentQuery.data;
  const headerTitle = agent ? `Launch: ${agent.name}` : "Launch agent";
  const pageTitle = agent
    ? `Roboto - Launch ${agent.name}`
    : "Roboto - Launch agent";

  if (agentQuery.isError) {
    return (
      <PageLayout title={pageTitle} headerContentLeft={headerTitle}>
        <div className={styles.container}>
          <div className={styles.error}>
            Failed to load agent. {agentQuery.error?.message ?? ""}
          </div>
        </div>
      </PageLayout>
    );
  }

  if (agentQuery.isLoading || agent === undefined) {
    return (
      <PageLayout title={pageTitle} headerContentLeft={headerTitle}>
        <div className={styles.container}>Loading agent&hellip;</div>
      </PageLayout>
    );
  }

  const headerActions = (
    <div className={styles.actions}>
      <Button
        variant="text"
        onClick={() => navigate(`/agents/${agent.agent_id}`)}
      >
        Cancel
      </Button>
      <Button
        variant="contained"
        disabled={!canSubmit}
        onClick={() => {
          void handleSubmit();
        }}
      >
        {launchMutation.isPending ? "Launching…" : "Launch"}
      </Button>
    </div>
  );

  return (
    <PageLayout
      title={pageTitle}
      headerContentLeft={headerTitle}
      headerContentRight={headerActions}
    >
      <div className={styles.container}>
        {agent.description && (
          <p className={styles.muted}>{agent.description}</p>
        )}

        <section className={styles.section}>
          {agent.variables.length === 0 ? (
            <p className={styles.muted}>
              This agent has no declared variables. Click <em>Launch</em> to
              launch it as-is.
            </p>
          ) : (
            agent.variables.map((variable) => (
              <VariableInput
                key={variable.name}
                variable={variable}
                value={values[variable.name] ?? ""}
                currentOrg={currentOrganization ?? null}
                onChange={(next) =>
                  setValues((prev) => ({ ...prev, [variable.name]: next }))
                }
              />
            ))
          )}
        </section>

        <section className={styles.section}>
          <div className={styles.field}>
            <span className={styles.label}>Visibility</span>
            <div className={styles.visibilityRow}>
              <label>
                <input
                  type="radio"
                  name="visibility"
                  value={SessionVisibility.ORG}
                  checked={visibility === SessionVisibility.ORG}
                  onChange={() => setVisibility(SessionVisibility.ORG)}
                />{" "}
                Org &mdash; visible on the Threads tab to everyone in your org.
              </label>
              <label>
                <input
                  type="radio"
                  name="visibility"
                  value={SessionVisibility.PRIVATE}
                  checked={visibility === SessionVisibility.PRIVATE}
                  onChange={() => setVisibility(SessionVisibility.PRIVATE)}
                />{" "}
                Private &mdash; only you (and Roboto admins) can read this
                thread.
              </label>
            </div>
          </div>
        </section>

        {submitError && <div className={styles.error}>{submitError}</div>}
      </div>
    </PageLayout>
  );
}

function VariableInput({
  variable,
  value,
  currentOrg,
  onChange,
}: {
  variable: TemplateVariable;
  value: string;
  currentOrg: OrgRecord | null;
  onChange: (next: string) => void;
}) {
  const isDatasetVariable = variable.type === TemplateVariableType.DATASET_ID;
  return (
    <div className={styles.field}>
      <label className={styles.label} htmlFor={`variable-${variable.name}`}>
        {variable.name}
        {variable.required && (
          <span className={styles.required} aria-label="required">
            *
          </span>
        )}
        <span className={styles.typeBadge}>{variable.type}</span>
      </label>
      {isDatasetVariable ? (
        <DatasetSelector
          datasetId={value === "" ? null : value}
          setDatasetId={(next) => onChange(next ?? "")}
          currentOrg={currentOrg}
        />
      ) : (
        <input
          id={`variable-${variable.name}`}
          className={styles.input}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={
            variable.default !== null && variable.default !== undefined
              ? variable.default
              : undefined
          }
        />
      )}
      {variable.description && (
        <p className={styles.helpText}>{variable.description}</p>
      )}
    </div>
  );
}
